'use client';

import { useState, type ReactNode } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Code as CodeIcon,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Unlink,
} from 'lucide-react';
import type { Editor } from '@tiptap/react';
import { Button } from '@/components/ui/button/button';
import { Input } from '@/components/ui/input/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/popover';
import { usePageEditorStore } from '../store/usePageEditor';

interface Props {
  blockId: string;
  initialHtml: string;
  path: string;
}

export function RichtextTiptap({ blockId, initialHtml, path }: Props) {
  const updateBlockPath = usePageEditorStore((s) => s.updateBlockPath);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: initialHtml,
    onUpdate: ({ editor }) => updateBlockPath(blockId, path, editor.getHTML()),
    immediatelyRender: false,
  });

  if (!editor) return null;

  return (
    <>
      <BubbleMenu
        editor={editor}
        className="flex items-center gap-0.5 rounded-md border bg-background p-1 shadow-md"
      >
        <ToolButton
          editor={editor}
          isActive={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label="Bold"
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton
          editor={editor}
          isActive={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label="Italic"
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton
          editor={editor}
          isActive={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          label="Underline"
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton
          editor={editor}
          isActive={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          label="Strikethrough"
        >
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolButton>

        <span className="mx-1 h-5 w-px bg-border" />

        <ToolButton
          editor={editor}
          isActive={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          label="Heading 2"
        >
          <Heading2 className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton
          editor={editor}
          isActive={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          label="Heading 3"
        >
          <Heading3 className="h-3.5 w-3.5" />
        </ToolButton>

        <span className="mx-1 h-5 w-px bg-border" />

        <ToolButton
          editor={editor}
          isActive={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label="Bullet list"
        >
          <List className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton
          editor={editor}
          isActive={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label="Numbered list"
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton
          editor={editor}
          isActive={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          label="Quote"
        >
          <Quote className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton
          editor={editor}
          isActive={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
          label="Inline code"
        >
          <CodeIcon className="h-3.5 w-3.5" />
        </ToolButton>

        <span className="mx-1 h-5 w-px bg-border" />

        <ToolButton
          editor={editor}
          isActive={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          label="Align left"
        >
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton
          editor={editor}
          isActive={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          label="Align center"
        >
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolButton>
        <ToolButton
          editor={editor}
          isActive={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          label="Align right"
        >
          <AlignRight className="h-3.5 w-3.5" />
        </ToolButton>

        <span className="mx-1 h-5 w-px bg-border" />

        <LinkPopover editor={editor} />
      </BubbleMenu>
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none [&_*]:outline-none focus-within:outline-none"
      />
    </>
  );
}

function ToolButton({
  isActive,
  onClick,
  label,
  children,
}: {
  editor: Editor;
  isActive: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <Button
      size="icon"
      variant={isActive ? 'default' : 'ghost'}
      className="h-7 w-7"
      onClick={onClick}
      aria-label={label}
      title={label}
    >
      {children}
    </Button>
  );
}

function LinkPopover({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const isActive = editor.isActive('link');
  const currentHref = (editor.getAttributes('link').href as string) ?? '';
  const [draft, setDraft] = useState(currentHref);

  const apply = () => {
    if (!draft) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: draft }).run();
    }
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setDraft(currentHref);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant={isActive ? 'default' : 'ghost'}
          className="h-7 w-7"
          aria-label={isActive ? 'Edit link' : 'Add link'}
          title={isActive ? 'Edit link' : 'Add link'}
        >
          <LinkIcon className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        <div className="space-y-2">
          <Input
            value={draft}
            placeholder="https://example.com"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                apply();
              }
            }}
            autoFocus
          />
          <div className="flex justify-between gap-2">
            {isActive ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  editor.chain().focus().extendMarkRange('link').unsetLink().run();
                  setOpen(false);
                }}
                aria-label="Remove link"
              >
                <Unlink className="mr-1 h-3.5 w-3.5" />
                Remove
              </Button>
            ) : (
              <span />
            )}
            <Button size="sm" onClick={apply} disabled={!draft}>
              {isActive ? 'Update' : 'Add link'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
